import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import StatusBadge from '../components/StatusBadge';
import { fetchHost } from '../lib/api';

export default function HostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (id) {
      fetchHost(Number(id))
        .then(setData)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id]);

  if (loading) {
    return <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />;
  }

  if (!data) {
    return <p className="text-gray-500 dark:text-gray-400">{t('hosts.notFound')}</p>;
  }

  const fleet = data.fleet;

  return (
    <div className="space-y-6">
      <Link to="/hosts" className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
        <ArrowLeft className="w-4 h-4" /> {t('hosts.backToHosts')}
      </Link>

      {/* Host overview */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{fleet.hostname}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {fleet.platform} &middot; {fleet.os_version}
            </p>
          </div>
          <StatusBadge status={fleet.status} size="md" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('hosts.cpu')}</span>
            <p className="font-medium text-gray-800 dark:text-gray-200">{fleet.cpu_brand || '-'}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('hosts.model')}</span>
            <p className="font-medium text-gray-800 dark:text-gray-200">{fleet.hardware_model || '-'}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('hosts.fleetId')}</span>
            <p className="font-medium text-gray-800 dark:text-gray-200">{fleet.id}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('hosts.uuid')}</span>
            <p className="font-medium font-mono text-xs text-gray-800 dark:text-gray-200">{fleet.uuid}</p>
          </div>
        </div>
      </div>

      {/* Wazuh agent info */}
      {data.wazuh_agent && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">{t('hosts.wazuhAgent')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('hosts.status')}</span>
              <p><StatusBadge status={data.wazuh_agent.status} /></p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('hosts.agentId')}</span>
              <p className="font-medium text-gray-800 dark:text-gray-200">{data.wazuh_agent.id}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('hosts.version')}</span>
              <p className="font-medium text-gray-800 dark:text-gray-200">{data.wazuh_agent.version}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('hosts.groups')}</span>
              <p className="font-medium text-gray-800 dark:text-gray-200">{data.wazuh_agent.group?.join(', ') || '-'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Policies */}
      {data.policies && data.policies.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">{t('hosts.policies')}</h3>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {data.policies.map((p: any) => (
              <div key={p.id} className="px-5 py-3 flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">{p.name}</span>
                <StatusBadge status={p.response || 'pending'} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SCA Summary */}
      {data.sca_summary && data.sca_summary.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">{t('hosts.scaSummary')}</h3>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {data.sca_summary.map((s: any) => (
              <div key={s.policy_id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{s.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('hosts.pass')}: {s.pass} | {t('hosts.fail')}: {s.fail} | {t('hosts.total')}: {s.total_checks}
                  </p>
                </div>
                <span className="text-lg font-bold text-gray-800 dark:text-gray-100">{s.score}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vulnerabilities */}
      {data.vulnerabilities && data.vulnerabilities.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
              {t('hosts.vulnerabilities')} ({data.vulnerabilities.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400">
                  <th className="px-4 py-2 text-left">{t('hosts.cve')}</th>
                  <th className="px-4 py-2 text-left">{t('hosts.package')}</th>
                  <th className="px-4 py-2 text-left">{t('hosts.severity')}</th>
                  <th className="px-4 py-2 text-left">{t('hosts.cvss3')}</th>
                </tr>
              </thead>
              <tbody>
                {data.vulnerabilities.slice(0, 20).map((v: any, i: number) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">{v.cve}</td>
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{v.name}</td>
                    <td className="px-4 py-2"><StatusBadge status={v.severity?.toLowerCase() || 'unknown'} /></td>
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{v.cvss3_score || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
